import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import jsPDF from "jspdf";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import "jspdf-autotable";
import { formatDMY } from "@/lib/dmy";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calculator,
  Download,
  Printer,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Package,
  AlertTriangle,
  FileX,
  DollarSign,
  Truck,
  Settings,
  FileSpreadsheet,
  FileDown
} from "lucide-react";

const PendingAutoGenerateReportPage = () => {
  usePageTitle("Pending Sale Auto Generate");
  const { token } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'total_orders', direction: 'desc' });
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [issueFilter, setIssueFilter] = useState("all"); // "all", "product", "shipping"
  const [exportMode, setExportMode] = useState("single");

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (issueFilter === "product") params.append('issue_type', 'product_not_found');
      if (issueFilter === "shipping") params.append('issue_type', 'service_not_found');
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/pending-auto-generate${params.toString() ? `?${params}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const from = urlParams.get('from');
    const to = urlParams.get('to');
    if (from) setDateFrom(from);
    if (to) setDateTo(to);
  }, []);

  useEffect(() => {
    if (token) fetchReport();
  }, [token, dateFrom, dateTo, issueFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-purple-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-purple-600" />;
  };

  const toggleClient = (code) => {
    setExpandedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  const filteredAndSortedClients = useMemo(() => {
    if (!reportData?.clients) return [];
    
    let filtered = reportData.clients;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.client_code?.toLowerCase().includes(term)
      );
    }
    
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      
      if (sortConfig.key === 'client_code') {
        return sortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [reportData, searchTerm, sortConfig]);

  const exportToCSV = (clientCode = null) => {
    if (!reportData?.clients) return;
    
    const clientsToExport = clientCode 
      ? filteredAndSortedClients.filter(c => c.client_code === clientCode)
      : filteredAndSortedClients;
    
    const allRows = [];
    
    clientsToExport.forEach(client => {
      allRows.push([`Client: ${client.client_code}`, `Total Orders: ${client.total_orders}`, `Missing Product Price: ${client.zero_medicine_cost}`, `Missing Shipping Rate: ${client.zero_shipping_cost}`]);
      allRows.push(["Tracking", "Order Date", "Product", "Qty", "Issue"]);
      
      client.orders?.forEach(order => {
        allRows.push([
          order.tracking,
          order.order_date,
          order.product,
          order.qty,
          order.issue_text
        ]);
      });
      
      allRows.push([]);
    });
    
    const csvContent = allRows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = clientCode 
      ? `pending_auto_generate_${clientCode}_${format(new Date(), 'yyyy-MM-dd')}.csv`
      : `pending_auto_generate_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(clientCode ? `Exported ${clientCode} data!` : "Report exported!");
  };

  const exportAllClientWise = () => {
    filteredAndSortedClients.forEach(client => {
      if (client.orders?.length > 0) {
        exportToCSV(client.client_code);
      }
    });
    toast.success(`Exported ${filteredAndSortedClients.length} client files!`);
  };

  // PDF Export Functions
  const exportToPDF = (clientCode = null) => {
    if (!reportData?.clients) return;
    
    const clientsToExport = clientCode 
      ? filteredAndSortedClients.filter(c => c.client_code === clientCode)
      : filteredAndSortedClients;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Pending Sale Auto Generate Report", pageWidth / 2, 20, { align: "center" });
    
    // Subtitle
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const dateRangeText = dateFrom || dateTo 
      ? `Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}`
      : 'All Time';
    doc.text(dateRangeText, pageWidth / 2, 28, { align: "center" });
    doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy, HH:mm')}`, pageWidth / 2, 34, { align: "center" });
    
    // Summary
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Summary", 14, 45);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Total Clients with Issues: ${reportData.summary?.total_clients || 0}`, 14, 52);
    doc.text(`Total Orders with Issues: ${reportData.summary?.total_orders || 0}`, 14, 58);
    doc.text(`Product Not Found: ${reportData.summary?.product_not_found_count || 0}`, 14, 64);
    doc.text(`Shipping Rate Not Found: ${reportData.summary?.service_not_found_count || 0}`, 14, 70);
    
    let yPos = 85;
    
    clientsToExport.forEach((client, idx) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      // Client header
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setFillColor(237, 233, 254); // Purple light
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.text(`Client: ${client.client_code}`, 16, yPos);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(`Orders: ${client.total_orders} | Product Issues: ${client.zero_medicine_cost} | Shipping Issues: ${client.zero_shipping_cost}`, pageWidth - 14, yPos, { align: "right" });
      
      yPos += 10;
      
      // Orders table
      if (client.orders && client.orders.length > 0) {
        const tableData = client.orders.slice(0, 50).map(order => [
          order.tracking || '-',
          order.order_date || '-',
          (order.product || '-').substring(0, 30),
          order.qty || '-',
          (order.issue_text || '-').substring(0, 30)
        ]);
        
        doc.autoTable({
          startY: yPos,
          head: [['Tracking', 'Date', 'Product', 'Qty', 'Issue']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [147, 51, 234], fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 22 },
            2: { cellWidth: 55 },
            3: { cellWidth: 15 },
            4: { cellWidth: 45 }
          },
          margin: { left: 14, right: 14 }
        });
        
        yPos = doc.lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(9);
        doc.text("No order details available", 14, yPos);
        yPos += 15;
      }
    });
    
    // Footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
    }
    
    const filename = clientCode 
      ? `pending_auto_generate_${clientCode}_${format(new Date(), 'yyyy-MM-dd')}.pdf`
      : `pending_auto_generate_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    
    doc.save(filename);
    toast.success(clientCode ? `PDF exported for ${clientCode}!` : "PDF report exported!");
  };

  const exportAllClientWisePDF = () => {
    filteredAndSortedClients.forEach(client => {
      if (client.orders?.length > 0) {
        exportToPDF(client.client_code);
      }
    });
    toast.success(`Exported ${filteredAndSortedClients.length} client PDFs!`);
  };

  const handlePrint = () => {
    window.print();
  };

  const applyDateFilter = (preset) => {
    const today = new Date();
    let from = "";
    let to = format(today, "yyyy-MM-dd");
    
    switch (preset) {
      case "30days":
        from = format(new Date(today.setDate(today.getDate() - 30)), "yyyy-MM-dd");
        break;
      case "thisMonth":
        from = format(new Date(today.getFullYear(), today.getMonth(), 1), "yyyy-MM-dd");
        break;
      case "lastMonth":
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        from = format(lastMonth, "yyyy-MM-dd");
        to = format(new Date(today.getFullYear(), today.getMonth(), 0), "yyyy-MM-dd");
        break;
      case "all":
        from = "";
        to = "";
        break;
    }
    
    setDateFrom(from);
    setDateTo(to);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 print:p-2">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          table { font-size: 10px; }
          th, td { padding: 4px 6px !important; }
        }
      `}</style>

      {/* Header */}
      <div className="no-print sticky top-0 z-20 bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Calculator className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Pending Sale Auto Generate</h1>
                <p className="text-sm text-muted-foreground">
                  DP Clients/Agents with missing price list or shipping rates
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Select value={exportMode} onValueChange={setExportMode}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single File</SelectItem>
                  <SelectItem value="client-wise">Client-wise</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportMode === "single" ? exportToCSV() : exportAllClientWise()}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export CSV {exportMode === "client-wise" && "(Client-wise)"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportMode === "single" ? exportToPDF() : exportAllClientWisePDF()}>
                    <FileDown className="w-4 h-4 mr-2" />
                    Export PDF {exportMode === "client-wise" && "(Client-wise)"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handlePrint}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print Report
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          {/* Filters */}
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Quick Filter:</Label>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("all")} 
                  className={!dateFrom && !dateTo ? "bg-purple-100" : ""}>All Time</Button>
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("30days")}>Last 30 Days</Button>
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("thisMonth")}>This Month</Button>
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("lastMonth")}>Last Month</Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Issue Type:</Label>
              <Select value={issueFilter} onValueChange={setIssueFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Issues</SelectItem>
                  <SelectItem value="product">Product Not Found</SelectItem>
                  <SelectItem value="shipping">Shipping Not Found</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">From:</Label>
              <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">To:</Label>
              <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto p-6 print:p-2 print:max-w-none">
        {/* Print Header */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold text-center">Pending Sale Auto Generate Report</h1>
          <p className="text-center text-gray-600">
            {dateFrom || dateTo ? `Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}` : 'All Time'}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-600" />
                <p className="text-xs font-medium text-purple-600 uppercase">Total Clients</p>
              </div>
              <p className="text-3xl font-bold text-purple-700 mt-2">
                {reportData?.summary?.total_clients || 0}
              </p>
              <p className="text-xs text-purple-500 mt-1">With auto-generation issues</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                <p className="text-xs font-medium text-muted-foreground uppercase">Total Orders</p>
              </div>
              <p className="text-3xl font-bold mt-2">
                {(reportData?.summary?.total_orders || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Orders with issues</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-red-600" />
                <p className="text-xs font-medium text-muted-foreground uppercase">Product Not Found</p>
              </div>
              <p className="text-3xl font-bold mt-2 text-red-600">
                {(reportData?.summary?.product_not_found_count || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Need price list update</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-600" />
                <p className="text-xs font-medium text-muted-foreground uppercase">Shipping Not Found</p>
              </div>
              <p className="text-3xl font-bold mt-2 text-orange-600">
                {(reportData?.summary?.service_not_found_count || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Need shipping rate update</p>
            </CardContent>
          </Card>
        </div>

        {/* Issue Breakdown */}
        {reportData?.issue_breakdown && reportData.issue_breakdown.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Issue Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 flex-wrap">
                {reportData.issue_breakdown.map((issue, idx) => (
                  <Badge key={idx} variant="outline" className="px-3 py-1 text-sm">
                    {issue.type}: <span className="font-bold ml-1">{issue.count}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="no-print flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by client code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Badge variant="outline" className="px-3 py-1">
            {filteredAndSortedClients.length} clients
          </Badge>
        </div>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            {filteredAndSortedClients.length === 0 ? (
              <div className="p-12 text-center">
                <FileX className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">No Auto-Generation Issues Found</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  All orders have successfully generated sales data.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-purple-50 dark:bg-purple-950/30">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="cursor-pointer hover:bg-purple-100/50" onClick={() => handleSort('client_code')}>
                      <div className="flex items-center">
                        Client Code
                        <SortIcon columnKey="client_code" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-purple-100/50" onClick={() => handleSort('total_orders')}>
                      <div className="flex items-center">
                        Total Orders
                        <SortIcon columnKey="total_orders" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-purple-100/50" onClick={() => handleSort('zero_medicine_cost')}>
                      <div className="flex items-center">
                        Product Issues
                        <SortIcon columnKey="zero_medicine_cost" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-purple-100/50" onClick={() => handleSort('zero_shipping_cost')}>
                      <div className="flex items-center">
                        Shipping Issues
                        <SortIcon columnKey="zero_shipping_cost" />
                      </div>
                    </TableHead>
                    <TableHead className="no-print">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedClients.map((client) => (
                    <Collapsible key={client.client_code} asChild>
                      <>
                        <TableRow className="hover:bg-purple-50/50 cursor-pointer" onClick={() => toggleClient(client.client_code)}>
                          <TableCell>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                                {expandedClients.has(client.client_code) 
                                  ? <ChevronDown className="w-4 h-4" />
                                  : <ChevronRight className="w-4 h-4" />
                                }
                              </Button>
                            </CollapsibleTrigger>
                          </TableCell>
                          <TableCell className="font-medium">{client.client_code}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-purple-50">{client.total_orders}</Badge>
                          </TableCell>
                          <TableCell>
                            {client.zero_medicine_cost > 0 ? (
                              <Badge variant="destructive" className="bg-red-100 text-red-700">
                                <DollarSign className="w-3 h-3 mr-1" />
                                {client.zero_medicine_cost}
                              </Badge>
                            ) : (
                              <span className="text-green-600">✓</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {client.zero_shipping_cost > 0 ? (
                              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
                                <Truck className="w-3 h-3 mr-1" />
                                {client.zero_shipping_cost}
                              </Badge>
                            ) : (
                              <span className="text-green-600">✓</span>
                            )}
                          </TableCell>
                          <TableCell className="no-print">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>
                                  <Download className="w-3 h-3 mr-1" />
                                  Export
                                  <ChevronDown className="w-3 h-3 ml-1" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportToCSV(client.client_code); }}>
                                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                                  CSV
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportToPDF(client.client_code); }}>
                                  <FileDown className="w-4 h-4 mr-2" />
                                  PDF
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        <CollapsibleContent asChild>
                          <TableRow>
                            <TableCell colSpan={6} className="p-0 bg-gray-50 dark:bg-gray-900">
                              <div className="p-4">
                                <h4 className="font-medium mb-2 text-sm">Order Details for {client.client_code}</h4>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="text-xs">
                                      <TableHead>Tracking</TableHead>
                                      <TableHead>Order Date</TableHead>
                                      <TableHead>Product</TableHead>
                                      <TableHead>Qty</TableHead>
                                      <TableHead>Issue</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {client.orders?.slice(0, 20).map((order, idx) => (
                                      <TableRow key={idx} className="text-xs">
                                        <TableCell className="font-mono">{order.tracking || '-'}</TableCell>
                                        <TableCell>{formatDMY(order.order_date)}</TableCell>
                                        <TableCell className="max-w-[200px] truncate">{order.product || '-'}</TableCell>
                                        <TableCell>{order.qty || '-'}</TableCell>
                                        <TableCell>
                                          <Badge variant="outline" className={
                                            order.issue_text?.includes('Product') ? 'bg-red-50 text-red-700 border-red-200' :
                                            order.issue_text?.includes('Shipping') ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                            'bg-gray-50'
                                          }>
                                            {order.issue_text || '-'}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {client.orders?.length > 20 && (
                                      <TableRow>
                                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                                          ... and {client.orders.length - 20} more orders
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PendingAutoGenerateReportPage;
